import { useMutation, UseMutationOptions, useQuery } from "@tanstack/react-query";
import { useAppContext } from "contexts";

export type AdminNote = {
  id: string;
  userId: string;
  text: string;
  createdAt: string;
};

export type CreateAdminNoteInput = {
  userId: string;
  body: { text: string };
};

type UseCreateAdminNoteOptions = Omit<
  UseMutationOptions<AdminNote, Error, CreateAdminNoteInput>,
  "mutationFn" | "mutationKey"
>;

export type DeleteAdminNoteInput = { noteId: string };
export type DeleteAdminNoteResponse = { ok: boolean };

type UseDeleteAdminNoteOptions = Omit<
  UseMutationOptions<DeleteAdminNoteResponse, Error, DeleteAdminNoteInput>,
  "mutationFn" | "mutationKey"
>;

export const useCreateAdminNoteMutation = (options?: UseCreateAdminNoteOptions) => {
  const { apiService } = useAppContext();

  return useMutation<AdminNote, Error, CreateAdminNoteInput>({
    ...options,
    mutationKey: ["admin-notes", "create"],
    mutationFn: async ({ userId, body }) => {
      const response = await apiService.request(`/admin-notes/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });

      const data = await response?.json();

      if (!response?.ok) {
        throw new Error(data?.message ?? "Failed to create note");
      }

      return data as AdminNote;
    },
  });
};

export function useDeleteAdminNoteMutation(options?: UseDeleteAdminNoteOptions) {
  const { apiService } = useAppContext();

  return useMutation<DeleteAdminNoteResponse, Error, DeleteAdminNoteInput>({
    mutationKey: ["admin-notes", "delete"],
    ...options,
    mutationFn: async ({ noteId }) => {
      const response = await apiService.request(`/admin-notes/${noteId}`, {
        method: "DELETE",
      });

      const data = await response?.json();

      if (!response?.ok) {
        throw new Error(data?.message ?? "Failed to delete note");
      }

      return data as DeleteAdminNoteResponse;
    },
  });
}

export function useGetMyAdminNotesForUser({
  userId,
  enabled = false,
}: {
  userId?: string;
  enabled?: boolean;
}) {
  const { apiService } = useAppContext();

  return useQuery({
    queryKey: ["admin-notes", "user", userId ?? "none"],
    enabled: Boolean(enabled && userId),
    queryFn: async () => {
      if (!userId) return [];
      const response = await apiService.request(`/admin-notes/users/${userId}`, {
        method: "GET",
      });
      return (await response?.json()) as AdminNote[];
    },
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
}
